import { USER_TYPE_STRING } from "@constants/Enums";
import { ADMINUSER } from "@types/adminUser";
import { APP } from "@types/app";
import { BROADCASTGROUP } from "@types/broadcastGroup";
import { CONTRACT } from "@types/contract";
import { NOTIFICATION } from "@types/notification";
import { SKILLTAG } from "@types/skillTag";
import { TENANT } from "@types/tenant";
import { TENANTDEPARTMENT } from "@types/tenantDepartment";
import { USER } from "@types/user";
import { USERAPP } from "@types/userApp";
import { USERROLE } from "@types/userRole";
import { USERTENANT } from "@types/usertenant";

export const MapUser = (data): USER => ({
  id: data.id,
  uid: data.uid,
  tenant_code: data.tenant_code,
  employee_number: data.employee_number,
  first_name: data.first_name,
  last_name: data.last_name,
  first_furigana_name: data.first_furigana_name,
  last_furigana_name: data.last_furigana_name,
  email: data.email || "",
  secondary_email: data.secondary_email || "",
  profile_image_url: data.profile_image || data.avatar_url,
  user_type: data.user_type,
  login_id: data.login_id,
  login_password: data.login_password,
  is_display: data.is_display,
  card_image_urls: data.card_images
    ? data.card_images.map((image) => ({
        blobId: image.blob_id,
        url: image.url,
        downloaded: true,
      }))
    : [],
  company_name: data.company_name,
  company_tel: data.company_tel,
  company_fax: data.company_fax,
  personal_tel: data.personal_tel,
  secondary_phone_number: data.secondary_phone_number,
  company_address: data.company_address,
  company_postal_code: data.company_postal_code,
  company_home_page: data.company_home_page,
  date_of_receiving_info: data.date_of_receiving_info,
  position_name:
    data.external_departments?.[0]?.external_name?.trim() ||
    (data.user_type === USER_TYPE_STRING.KEYCLOAK_USER ||
    data.user_type === USER_TYPE_STRING.INTERNAL_USER
      ? data.internal_departments?.[0]?.position_name?.trim() || "未登録"
      : "未登録"),
  department_name:
    data.internal_departments?.[0]?.department_name?.trim() || "未登録",
  display_order: data.internal_departments?.[0]?.position_code,
  broadcast_group_code: data.broadcast_group?.code || "",
  user_apps: data.available_apps,
  role: data.role,
  notes: data.notes || "",
  created_at: data.created_at,
  tenant_keycloak_groups: data.tenant_keycloak_groups,
  user_keycloak_groups: data.keycloak_groups,
});

export const MapTenant = (data): TENANT => ({
  id: data.id,
  code: data.code,
  contract_count: data.contract_count,
  name: data.name,
  name_kana: data.name_kana,
  name_english: data.name_english || "",
  postal_code: data.postal_code,
  address: data.address,
  phone_number: data.phone_number,
  fax_number: data.fax_number,
  url: data.url,
  local_auth: data.local_auth,
  google_auth: data.google_auth,
  microsoft_365_auth: data.microsoft_365_auth,
  require_passcode_for_phonebook: data.require_passcode_for_phonebook,
  disable_call_recording: data.disable_call_recording,
  contracts: data.contracts,
  shoppers_cloud_url: data.shoppers_cloud_url,
  sub_directory: data.sub_directory,
  store_id: data.store_id,
  keycloak_groups: data.keycloak_groups || [],
  auth_by_phone_number: data.auth_by_phone_number,
});

export const MapNotification = (data): NOTIFICATION => ({
  id: data.id,
  title: data.title,
  content: data.content,
  status: data.status,
  start_date: data.start_date,
  end_date: data.end_date,
  created_at: data.created_at,
  updated_at: data.updated_at,
});

export const MapUserTenant = (data): USERTENANT => {
  const userTenantDepartmentData: USERTENANT = {
    id: data.id,
    tenant_department_id: data.department_id,
    department_name: data.department_name,
    position_name: data.position_name,
  };

  return userTenantDepartmentData;
};

export const MapTenantDepartment = (data): TENANTDEPARTMENT => ({
  id: data.id,
  tenant_id: data.tenant_id,
  name: data.name,
  parent: data.parent ? data.parent.id : null,
  name_kana: data.name_kana,
  code: data.code,
  display_order: data.display_order,
  department_level: data.department_level,
  description: data.description,
  children: data.children || [],
});

export const MapBroadcastGroup = (data): BROADCASTGROUP => {
  const broadcastGroupData: BROADCASTGROUP = {
    id: data.id,
    name: data.name,
    name_kana: data.name_kana,
    code: data.code,
    parent: data.parent ? data.parent.id : null,
    tenant_id: data.tenant_id,
  };
  return broadcastGroupData;
};

export const MapRoleData = (data) => {
  const roleData = {
    id: data.role,
  };

  return roleData;
};

export const MapUserRoleData = (data): USERROLE => {
  const userRoleData: USERROLE = {
    user_id: data.user_id,
    role_id: data.role_id,
    uid: data.uid,
    created_at: data.created_at,
    updated_at: data.updated_at,
  };
  return userRoleData;
};

export const MapAdminUser = (data): ADMINUSER => {
  const adminUserData: ADMINUSER = {
    tenant_id: data.id,
    name: data.name,
    name_kana: data.name_kana,
    login_id: data.login_id,
    login_password: data.login_password,
    email: data.email,
  };
  return adminUserData;
};

export const MapUserApp = (data): USERAPP => ({
  id: data.id,
  user_id: data.user_id,
  app_id: data.app_id,
  app_name: data.app_name,
  app_description: data.app_description,
});

export const MapApp = (data): APP => ({
  id: data.id,
  name: data.name,
  force_update: data.force_update,
  current_version: data.current_version,
  description: data.description,
});

export const MapUserSkillTagData = (data): SKILLTAG => {
  const userAppData = {
    id: data.id,
    name: data.name,
    description: data.description,
  };

  return userAppData;
};

export const MapContractData = (data): CONTRACT => ({
  id: data.id,
  app_name: data.app_name,
  app_id: data.app_id,
  tenant_id: data.tenant_id,
  status: data.status,
  unit_price: data.unit_price || 0,
  current_users: data.current_users || 0,
  contract_count: data.contract_count || 0,
  start_date: data.start_date,
  end_date: data.end_date,
});

export const MapContact = (data, position_name = ""): USER => ({
  id: data.id,
  uid: data.uid,
  tenant_code: data.tenant_id,
  user_type: data.user_type,
  employee_number: data.employee_number,
  name:
    data.first_name && data.last_name
      ? `${data.last_name} ${data.first_name}`
      : "",
  first_name: data.first_name,
  last_name: data.last_name,
  furigana_name:
    data.first_furigana_name && data.last_furigana_name
      ? `${data.last_furigana_name} ${data.first_furigana_name}`
      : "",
  first_furigana_name: data.first_furigana_name,
  last_furigana_name: data.last_furigana_name,
  email: data.email,
  card_image_urls: data.card_images
    ? data.card_images.map((image) => ({
        blobId: image.blob_id,
        url: image.url,
      }))
    : [],
  company_name: data.company_name,
  department_name: data.department_name,
  company_fax: data.company_fax,
  company_tel: data.company_tel,
  company_postal_code: data.company_postal_code,
  company_address: data.company_address,
  personal_tel: data.personal_tel,
  company_home_page: data.company_home_page,
  position_name: position_name,
  created_at: data.created_at,
});
